/**
 * specPrimitives.tsx — Shared form controls for the Product Specs editors.
 *
 * Styling mirrors ProposalBuilderPage.tsx: AURA canonical CSS vars
 * (--ivory-*, --line, --ink-*, --saffron, --forest). No Tailwind `primary`
 * classes. Inline styles only, matching the proposal-builder conventions.
 *
 * The `spec` block ({ title, kpiTiles[], specRows[] }) is shared by the
 * tank_variant / hp_model / swh_series / fixed_page shapes, so the KPI + spec-row
 * editors live here and are reused by every editor component.
 *
 * Every text input registers itself as the "focused field" so the shared
 * <PlaceholderPalette> inserts a {token} chip at the caret of whichever field
 * the author last touched.
 */

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Plus, Trash2 } from 'lucide-react';

// ─── Focus-target context (drives the placeholder palette) ────────────────────

interface FocusTarget {
  el: HTMLInputElement | HTMLTextAreaElement;
  setValue: (next: string) => void;
}

interface FocusCtx {
  register: (t: FocusTarget | null) => void;
  insertAtFocus: (token: string) => void;
}

const SpecFocusContext = createContext<FocusCtx | null>(null);

/** Wrap an editor in this so its inputs share one "last focused field" target. */
export function SpecFocusProvider({ children }: { children: ReactNode }) {
  const targetRef = useRef<FocusTarget | null>(null);

  const register = useCallback((t: FocusTarget | null) => {
    targetRef.current = t;
  }, []);

  const insertAtFocus = useCallback((token: string) => {
    const t = targetRef.current;
    const chip = `{${token}}`;
    if (!t) return;
    const el = t.el;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = el.value.slice(0, start) + chip + el.value.slice(end);
    t.setValue(next);
    // Restore caret just after the inserted chip on next tick.
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + chip.length;
        el.setSelectionRange(pos, pos);
      } catch {
        /* element may have unmounted — ignore */
      }
    });
  }, []);

  return (
    <SpecFocusContext.Provider value={{ register, insertAtFocus }}>
      {children}
    </SpecFocusContext.Provider>
  );
}

function useSpecFocus(): FocusCtx | null {
  return useContext(SpecFocusContext);
}

// ─── Field label (mirrors ProposalBuilderPage FieldLabel) ─────────────────────

export function FieldLabel({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 6 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: 'var(--ink-3)',
          }}
        >
          {label}
        </span>
        {hint && (
          <span style={{ fontSize: 11.5, color: 'var(--ink-3)', marginLeft: 6 }}>
            — {hint}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

export const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 34,
  padding: '0 10px',
  fontSize: 13,
  border: '1px solid var(--line)',
  borderRadius: 7,
  background: 'var(--ivory-0)',
  color: 'var(--ink-1)',
  outline: 'none',
  boxSizing: 'border-box',
};

// ─── Token-aware text input ───────────────────────────────────────────────────
// Registers itself as the focus target so the palette can insert a {token} chip.

export function SpecInput({
  value,
  onChange,
  placeholder,
  multiline,
  monospace,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  multiline?: boolean;
  monospace?: boolean;
}) {
  const focus = useSpecFocus();
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const onFocus = () => {
    if (ref.current) {
      focus?.register({ el: ref.current, setValue: onChange });
    }
  };

  const sharedStyle: React.CSSProperties = {
    ...inputStyle,
    ...(monospace
      ? { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5 }
      : {}),
  };

  if (multiline) {
    return (
      <textarea
        ref={(el) => {
          ref.current = el;
        }}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        placeholder={placeholder}
        spellCheck={false}
        style={{
          ...sharedStyle,
          height: 'auto',
          minHeight: 60,
          padding: '8px 10px',
          lineHeight: 1.5,
          resize: 'vertical',
        }}
      />
    );
  }

  return (
    <input
      ref={(el) => {
        ref.current = el;
      }}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      onFocus={onFocus}
      placeholder={placeholder}
      style={sharedStyle}
    />
  );
}

// ─── Placeholder palette ──────────────────────────────────────────────────────

export function PlaceholderPalette({ tokens }: { tokens: string[] }) {
  const focus = useSpecFocus();
  if (!tokens.length) {
    return (
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', padding: '4px 0' }}>
        This family has no per-SKU placeholders — values are entered verbatim.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '2px 0' }}>
      {tokens.map((t) => (
        <button
          key={t}
          type="button"
          onMouseDown={(e) => {
            // mousedown (not click) so the focused input keeps its selection.
            e.preventDefault();
            focus?.insertAtFocus(t);
          }}
          title={`Insert {${t}} at the focused field`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            height: 24,
            padding: '0 9px',
            borderRadius: 6,
            fontSize: 11.5,
            fontWeight: 700,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            background: '#EEF2FF',
            color: '#3730A3',
            border: '1px solid #C7D2FE',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {`{${t}}`}
        </button>
      ))}
    </div>
  );
}

// ─── KPI tiles editor (value / label / qualifier) ─────────────────────────────

export interface KpiTileShape {
  value: string;
  label: string;
  qualifier: string;
}

export function KpiTilesEditor({
  tiles,
  onChange,
}: {
  tiles: KpiTileShape[];
  onChange: (next: KpiTileShape[]) => void;
}) {
  const set = (i: number, patch: Partial<KpiTileShape>) =>
    onChange(tiles.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const add = () => onChange([...tiles, { value: '', label: '', qualifier: '' }]);
  const remove = (i: number) => onChange(tiles.filter((_, idx) => idx !== i));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {tiles.map((t, i) => (
        <div
          key={i}
          style={{
            border: '1px solid var(--line)',
            borderRadius: 9,
            padding: 10,
            background: 'var(--ivory-0)',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                color: 'var(--ink-3)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              KPI {i + 1}
            </span>
            <button
              type="button"
              onClick={() => remove(i)}
              title="Remove KPI tile"
              style={iconBtnStyle('#b91c1c')}
            >
              <Trash2 size={13} />
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <MiniLabel>Value</MiniLabel>
              <SpecInput value={t.value ?? ''} onChange={(v) => set(i, { value: v })} placeholder="e.g. {tds} ppm" />
            </div>
            <div>
              <MiniLabel>Label</MiniLabel>
              <SpecInput value={t.label ?? ''} onChange={(v) => set(i, { label: v })} placeholder="e.g. TDS Tolerance" />
            </div>
          </div>
          <div>
            <MiniLabel>Qualifier</MiniLabel>
            <SpecInput value={t.qualifier ?? ''} onChange={(v) => set(i, { qualifier: v })} placeholder="sub-line under the value" />
          </div>
        </div>
      ))}
      <AddRowButton onClick={add} label="Add KPI tile" />
    </div>
  );
}

// ─── Spec rows editor (sr · description · [{label,value}]) ─────────────────────

export interface SpecSubRowShape {
  label: string;
  value: string;
}
export interface SpecRowShape {
  sr: number;
  description: string;
  rows: SpecSubRowShape[];
}

export function SpecRowsEditor({
  rows,
  onChange,
}: {
  rows: SpecRowShape[];
  onChange: (next: SpecRowShape[]) => void;
}) {
  const setRow = (i: number, patch: Partial<SpecRowShape>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const setSub = (ri: number, si: number, patch: Partial<SpecSubRowShape>) =>
    onChange(
      rows.map((r, idx) =>
        idx === ri
          ? { ...r, rows: r.rows.map((s, sj) => (sj === si ? { ...s, ...patch } : s)) }
          : r,
      ),
    );
  const addRow = () =>
    onChange([...rows, { sr: rows.length + 1, description: '', rows: [{ label: '', value: '' }] }]);
  const removeRow = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const addSub = (ri: number) =>
    onChange(rows.map((r, idx) => (idx === ri ? { ...r, rows: [...r.rows, { label: '', value: '' }] } : r)));
  const removeSub = (ri: number, si: number) =>
    onChange(rows.map((r, idx) => (idx === ri ? { ...r, rows: r.rows.filter((_, sj) => sj !== si) } : r)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map((r, ri) => (
        <div
          key={ri}
          style={{
            border: '1px solid var(--line)',
            borderRadius: 10,
            padding: 12,
            background: 'var(--ivory-0)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <input
              type="number"
              value={Number.isFinite(r.sr) ? r.sr : ''}
              onChange={(e) => setRow(ri, { sr: Number(e.target.value) })}
              title="SR number"
              style={{ ...inputStyle, width: 56, textAlign: 'center' }}
            />
            <div style={{ flex: 1 }}>
              <SpecInput
                value={r.description ?? ''}
                onChange={(v) => setRow(ri, { description: v })}
                placeholder="Row description (e.g. Inner Tank Specifications)"
              />
            </div>
            <button
              type="button"
              onClick={() => removeRow(ri)}
              title="Remove row group"
              style={iconBtnStyle('#b91c1c')}
            >
              <Trash2 size={13} />
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 8 }}>
            {r.rows.map((s, si) => (
              <div key={si} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: '38%' }}>
                  <SpecInput value={s.label ?? ''} onChange={(v) => setSub(ri, si, { label: v })} placeholder="label (optional)" />
                </div>
                <div style={{ flex: 1 }}>
                  <SpecInput value={s.value ?? ''} onChange={(v) => setSub(ri, si, { value: v })} placeholder="value (may contain {token})" />
                </div>
                <button
                  type="button"
                  onClick={() => removeSub(ri, si)}
                  title="Remove sub-row"
                  style={iconBtnStyle('var(--ink-3)')}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
            <AddRowButton onClick={() => addSub(ri)} label="Add sub-row" small />
          </div>
        </div>
      ))}
      <AddRowButton onClick={addRow} label="Add row group" />
    </div>
  );
}

// ─── The shared `spec` editor (title + KPI tiles + spec rows) ──────────────────

export interface CategorySpecShape {
  title: string;
  kpiTiles: KpiTileShape[];
  specRows: SpecRowShape[];
}

export function CategorySpecEditor({
  spec,
  tokens,
  onChange,
}: {
  spec: CategorySpecShape;
  tokens: string[];
  onChange: (next: CategorySpecShape) => void;
}) {
  return (
    <>
      <FieldLabel label="Page title" hint="may contain {token} (e.g. {modelLabel})">
        <SpecInput value={spec.title ?? ''} onChange={(v) => onChange({ ...spec, title: v })} />
      </FieldLabel>

      <FieldLabel label="Placeholders" hint="click a chip to insert it at the focused field">
        <PlaceholderPalette tokens={tokens} />
      </FieldLabel>

      <FieldLabel label="Hero KPI tiles">
        <KpiTilesEditor tiles={spec.kpiTiles ?? []} onChange={(kpiTiles) => onChange({ ...spec, kpiTiles })} />
      </FieldLabel>

      <FieldLabel label="Specification rows">
        <SpecRowsEditor rows={spec.specRows ?? []} onChange={(specRows) => onChange({ ...spec, specRows })} />
      </FieldLabel>
    </>
  );
}

// ─── Small shared bits ────────────────────────────────────────────────────────

export function MiniLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'var(--ink-3)',
        marginBottom: 3,
      }}
    >
      {children}
    </div>
  );
}

export function AddRowButton({
  onClick,
  label,
  small,
}: {
  onClick: () => void;
  label: string;
  small?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: small ? 28 : 32,
        padding: '0 12px',
        borderRadius: 7,
        fontSize: small ? 12 : 12.5,
        fontWeight: 600,
        border: '1px dashed var(--line-strong)',
        background: 'var(--ivory-0)',
        color: 'var(--ink-2)',
        cursor: 'pointer',
        alignSelf: 'flex-start',
      }}
    >
      <Plus size={13} /> {label}
    </button>
  );
}

export function iconBtnStyle(color: string): React.CSSProperties {
  return {
    width: 26,
    height: 26,
    border: '1px solid var(--line)',
    borderRadius: 6,
    background: 'var(--ivory-0)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color,
    flexShrink: 0,
    padding: 0,
  };
}

/** Generic key→value object editor (used for HP/R290/EVI per-model `values`). */
export function ValuesMapEditor({
  values,
  tokens,
  onChange,
}: {
  values: Record<string, string>;
  tokens: string[];
  onChange: (next: Record<string, string>) => void;
}) {
  const keys = Object.keys(values ?? {});
  const [newKey, setNewKey] = useState('');
  const setVal = (k: string, v: string) => onChange({ ...values, [k]: v });
  const removeKey = (k: string) => {
    const next = { ...values };
    delete next[k];
    onChange(next);
  };
  const addKey = () => {
    const k = newKey.trim();
    if (!k || k in (values ?? {})) return;
    onChange({ ...values, [k]: '' });
    setNewKey('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <PlaceholderPalette tokens={tokens} />
      {keys.map((k) => (
        <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div
            style={{
              width: '34%',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--ink-2)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={k}
          >
            {k}
          </div>
          <div style={{ flex: 1 }}>
            <SpecInput value={values[k] ?? ''} onChange={(v) => setVal(k, v)} />
          </div>
          <button type="button" onClick={() => removeKey(k)} title="Remove field" style={iconBtnStyle('var(--ink-3)')}>
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
        <input
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          placeholder="new field key (e.g. heatingCapacity)"
          style={{ ...inputStyle, flex: 1 }}
        />
        <AddRowButton onClick={addKey} label="Add field" small />
      </div>
    </div>
  );
}
